export const checkupStorageKey = 'finance-learning-checkups-v1';
const maxAttempts = 1000000;
const isRecord = value => value !== null && typeof value === 'object' && !Array.isArray(value);
const own = (record, key) => isRecord(record) ? Object.getOwnPropertyDescriptor(record, key)?.value : undefined;
const text = value => typeof value === 'string' && value.trim().length > 0;
const integer = (value, min, max) => Number.isInteger(value) && value >= min && value <= max;

function questionsFor(caseItem) {
  const questions = own(caseItem, 'questions');
  if (!text(own(caseItem, 'id')) || !Array.isArray(questions) || !questions.length) return null;
  const ids = new Set();
  for (const question of questions) {
    const id = own(question, 'id'), options = own(question, 'options');
    if (!text(id) || ids.has(id) || !text(own(question, 'lessonId'))
      || !Array.isArray(options) || options.length !== 3 || ![...options].every(text)
      || new Set(options.map(option => option.trim())).size !== 3 || !integer(own(question, 'answer'), 0, 2)) return null;
    ids.add(id);
  }
  return questions;
}

/** Only own integer selections count. Missing, inherited or invalid selections stay unanswered. */
export function gradeCheckup(caseItem, answersObject) {
  const questions = questionsFor(caseItem);
  if (!questions) return null;
  const results = questions.map(question => {
    const selection = own(answersObject, question.id);
    const selected = integer(selection, 0, 2) ? selection : null;
    return { questionId: question.id, lessonId: question.lessonId, selected, correct: selected === question.answer, answer: question.answer };
  });
  const answered = results.filter(result => result.selected !== null).length;
  const correct = results.filter(result => result.correct).length;
  return { answered, correct, total: questions.length, complete: answered === questions.length, results };
}

function timestamp(value) {
  if (typeof value !== 'string') return null;
  const parts = value.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.(\d{1,3}))?(?:Z|[+-]\d{2}:\d{2})$/);
  if (!parts) return null;
  const [, year, month, day, hour, minute, second] = parts.map(Number);
  const date = new Date(0);
  date.setUTCFullYear(year, month - 1, day); date.setUTCHours(hour, minute, second, 0);
  if (date.getUTCFullYear() !== year || date.getUTCMonth() !== month - 1 || date.getUTCDate() !== day
    || date.getUTCHours() !== hour || date.getUTCMinutes() !== minute || date.getUTCSeconds() !== second) return null;
  const time = Date.parse(value);
  return Number.isFinite(time) ? new Date(time).toISOString() : null;
}

function normalizeResult(raw, total) {
  const latest = own(raw, 'latest'), best = own(raw, 'best'), attempts = own(raw, 'attempts');
  const completedAt = timestamp(own(raw, 'completedAt'));
  if (!integer(latest, 0, total) || !integer(best, latest, total)
    || !integer(attempts, 1, maxAttempts) || !completedAt) return null;
  return { latest, best, attempts, completedAt };
}

function catalogFor(cases) {
  if (!Array.isArray(cases)) return null;
  const catalog = new Map();
  for (const caseItem of cases) {
    const questions = questionsFor(caseItem);
    if (!questions || catalog.has(caseItem.id)) return null;
    catalog.set(caseItem.id, questions.length);
  }
  return catalog;
}

function normalizeHistory(history, catalog) {
  return Object.fromEntries(Object.keys(history).flatMap(id => {
    if (!catalog.has(id)) return [];
    const result = normalizeResult(own(history, id), catalog.get(id));
    return result ? [[id, result]] : [];
  }));
}

/** No storage/network side effects: add a fully checked completed attempt to a new history. */
export function recordCheckupAttempt(history, caseItem, result, completedAt) {
  const questions = questionsFor(caseItem), time = timestamp(completedAt);
  const rows = own(result, 'results');
  if (!questions || !time || !Array.isArray(rows) || rows.length !== questions.length) return null;
  const answers = Object.fromEntries(rows.map(row => [own(row, 'questionId'), own(row, 'selected')]));
  const verified = gradeCheckup(caseItem, answers);
  if (!verified.complete || ['answered', 'correct', 'total', 'complete'].some(key => own(result, key) !== verified[key])
    || rows.some((row, index) => ['questionId', 'lessonId', 'selected', 'correct', 'answer'].some(key => own(row, key) !== verified.results[index][key]))) return null;
  // Other cases were validated when read. Retain only structurally valid own records;
  // this function knows the current case's exact question count, not other curricula.
  const previous = isRecord(history) ? Object.fromEntries(Object.keys(history).flatMap(id => {
    const value = normalizeResult(own(history, id), id === caseItem.id ? questions.length : Number.MAX_SAFE_INTEGER);
    return value ? [[id, value]] : [];
  })) : {};
  const last = own(previous, caseItem.id);
  return { ...previous, [caseItem.id]: {
    latest: verified.correct, best: Math.max(last?.best || 0, verified.correct),
    attempts: Math.min((last?.attempts || 0) + 1, maxAttempts), completedAt: time,
  } };
}

/** Invalid individual records are ignored; an unreadable root/schema is reported. */
export function readCheckupHistory(storage, cases) {
  try {
    const catalog = catalogFor(cases);
    if (!catalog || !storage || typeof storage.getItem !== 'function') throw new Error('Unavailable history');
    const stored = storage.getItem(checkupStorageKey);
    if (stored === null) return { history: {}, storageError: false };
    if (typeof stored !== 'string') throw new Error('Invalid stored history');
    const raw = JSON.parse(stored), history = own(raw, 'results');
    if (own(raw, 'version') !== 1 || !isRecord(history)) throw new Error('Unsupported history schema');
    return { history: normalizeHistory(history, catalog), storageError: false };
  } catch { return { history: {}, storageError: true }; }
}

export function saveCheckupHistory(storage, history, cases) {
  try {
    const catalog = catalogFor(cases);
    if (!catalog || !isRecord(history) || !storage || typeof storage.setItem !== 'function') return false;
    storage.setItem(checkupStorageKey, JSON.stringify({ version: 1, results: normalizeHistory(history, catalog) }));
    return true;
  } catch { return false; }
}
